import React, { useMemo, useState } from "react";
import {
    Button,
    Card,
    DatePicker,
    Descriptions,
    Empty,
    Modal,
    Space,
    Table,
    Tag,
    Typography,
} from "antd";

import {
    formatMetricHeader,
    formatMetricValue,
    getQualityStatusColor,
    normalizeMetricSchema,
} from "../../services/marketReferencePriceViewModel";
import { EMPTY_VALUE, formatDateTime } from "../../utils/formatters";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const DailyQuoteHistoryPanel = ({
    selectedSeries,
    selectedSeriesLoading,
    quoteFilters,
    setQuoteFilters,
    resetQuoteFilters,
    quotes,
    quotesLoading,
    quotePagination,
    onQuoteTableChange,
}) => {
    const [previewQuote, setPreviewQuote] = useState(null);

    const metricSchema = useMemo(
        () => normalizeMetricSchema(selectedSeries?.field_schema || [], quotes),
        [quotes, selectedSeries?.field_schema]
    );

    const columns = useMemo(() => {
        const baseColumns = [
            {
                title: "报价日期",
                dataIndex: "quote_date",
                key: "quote_date",
                width: 120,
                fixed: "left",
            },
            ...metricSchema.map((field) => ({
                title: formatMetricHeader(field),
                key: field.key,
                width: 160,
                render: (_, record) => formatMetricValue(record.metrics?.[field.key], field),
            })),
            {
                title: "质量状态",
                dataIndex: "data_quality_status",
                key: "data_quality_status",
                width: 110,
                render: (value) => <Tag color={getQualityStatusColor(value)}>{value || EMPTY_VALUE}</Tag>,
            },
            {
                title: "发布时间",
                dataIndex: "published_at",
                key: "published_at",
                width: 160,
                render: (value) => formatDateTime(value, "YYYY-MM-DD HH:mm"),
            },
            {
                title: "备注",
                dataIndex: "remarks",
                key: "remarks",
                width: 180,
                ellipsis: true,
                render: (value) => value || EMPTY_VALUE,
            },
            {
                title: "原始记录",
                key: "raw_record",
                width: 110,
                render: (_, record) => (
                    record.raw_record ? (
                        <Button type="link" size="small" onClick={() => setPreviewQuote(record)}>
                            查看
                        </Button>
                    ) : EMPTY_VALUE
                ),
            },
        ];

        return baseColumns;
    }, [metricSchema]);

    if (!selectedSeries && !selectedSeriesLoading) {
        return (
            <Card title="日度报价历史" size="small" style={{ height: "100%" }}>
                <Empty description="请先在左侧选择一条 market series" />
            </Card>
        );
    }

    return (
        <>
            <Card
                title="日度报价历史"
                size="small"
                style={{ height: "100%" }}
                extra={<Text type="secondary">切换曲线或日期范围后会刷新历史表</Text>}
            >
                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                    <Descriptions column={2} size="small" bordered>
                        <Descriptions.Item label="曲线名称">{selectedSeries?.series_name || EMPTY_VALUE}</Descriptions.Item>
                        <Descriptions.Item label="来源">{selectedSeries?.source?.source_name || EMPTY_VALUE}</Descriptions.Item>
                        <Descriptions.Item label="曲线代码">{selectedSeries?.series_code || EMPTY_VALUE}</Descriptions.Item>
                        <Descriptions.Item label="市场区域">{selectedSeries?.market_region || EMPTY_VALUE}</Descriptions.Item>
                        <Descriptions.Item label="报价口径">{`${selectedSeries?.quote_currency || EMPTY_VALUE}/${selectedSeries?.quote_unit || EMPTY_VALUE}`}</Descriptions.Item>
                        <Descriptions.Item label="交付口径">{selectedSeries?.delivery_basis || EMPTY_VALUE}</Descriptions.Item>
                        <Descriptions.Item label="覆盖范围">
                            {selectedSeries?.earliest_quote_date && selectedSeries?.latest_quote_date
                                ? `${selectedSeries.earliest_quote_date} ~ ${selectedSeries.latest_quote_date}`
                                : EMPTY_VALUE}
                        </Descriptions.Item>
                        <Descriptions.Item label="报价条数">{selectedSeries?.quote_count ?? EMPTY_VALUE}</Descriptions.Item>
                        <Descriptions.Item label="字段 Schema" span={2}>
                            <Space size={[4, 4]} wrap>
                                {metricSchema.length
                                    ? metricSchema.map((field) => (
                                        <Tag key={field.key} color="cyan">
                                            {formatMetricHeader(field)}
                                        </Tag>
                                    ))
                                    : EMPTY_VALUE}
                            </Space>
                        </Descriptions.Item>
                    </Descriptions>

                    <Space wrap>
                        <RangePicker
                            value={quoteFilters.dateRange}
                            onChange={(value) => {
                                setQuoteFilters({ dateRange: value });
                            }}
                        />
                        <Button onClick={resetQuoteFilters}>清空日期范围</Button>
                    </Space>

                    <Table
                        rowKey="id"
                        size="small"
                        loading={quotesLoading || selectedSeriesLoading}
                        columns={columns}
                        dataSource={quotes}
                        pagination={quotePagination}
                        onChange={onQuoteTableChange}
                        scroll={{ x: "max-content" }}
                        locale={{ emptyText: "当前条件下暂无日度报价" }}
                    />
                </Space>
            </Card>

            <Modal
                title={previewQuote ? `${previewQuote.quote_date} 原始记录` : "原始记录"}
                open={!!previewQuote}
                footer={null}
                width={760}
                onCancel={() => setPreviewQuote(null)}
            >
                <pre
                    style={{
                        margin: 0,
                        padding: 12,
                        overflowX: "auto",
                        background: "#f7f7f7",
                        borderRadius: 6,
                        fontSize: 12,
                    }}
                >
                    {previewQuote?.raw_record ? JSON.stringify(previewQuote.raw_record, null, 2) : EMPTY_VALUE}
                </pre>
            </Modal>
        </>
    );
};

export default DailyQuoteHistoryPanel;
